import { company } from '~/company/Company';
import { CompanyName } from '~/company/interface';
import type { CloudflareResizeOptions } from '~/components/CloudflareResponsiveImage/CloudflareResizeOptions';

export const AUTOPLAY_DELAY = 5000;

interface HeroBannerSize {
  width: number;
  height: number;
}

/**
 * Hero banner image dimensions per company.
 *
 * Mirrors the CMS `BANNER_LINK` aspect-ratio config
 * (`apps/cms/.../Categories/components/Images/constants.ts`) — the value the
 * editor is forced to upload against. The container aspect-ratio
 * (`HeroBannerFrame`), the Cloudflare request size and the `<img>`
 * width/height all derive from these numbers, so they stay in sync and never
 * cause a layout shift.
 *
 * Must stay a function: on the server `company` is still the CityDrinks default
 * until `entry-server` assigns it per request, while in the browser the
 * `Company` constructor already read the tenant from `__INITIAL_STATE__`. A
 * module-level constant therefore resolved to the default during SSR and to the
 * real tenant after hydration — MCosmetic shipped HTML asking the CDN for
 * 360 / 1920x888 and then re-requested 390 / 1920x848 on the client, a
 * mismatch on the LCP image.
 */
const getHeroBannerSizes = (): Record<'desktop' | 'mobile', HeroBannerSize> =>
  company.match({
    [CompanyName.MCosmetic]: {
      desktop: { width: 1920, height: 848 },
      mobile: { width: 390, height: 492 },
    },
    default: {
      desktop: { width: 1920, height: 888 },
      mobile: { width: 360, height: 487 },
    },
  });

/** Hero banner CDN request params (see `getHeroBannerSizes` on why it is a function). */
export const getHeroBannerImageParams = (): Record<
  'desktop' | 'mobile',
  CloudflareResizeOptions
> => {
  const sizes = getHeroBannerSizes();

  return {
    desktop: { ...sizes.desktop, fit: 'cover' },
    mobile: { ...sizes.mobile, fit: 'cover' },
  };
};
